import React from 'react';
import { useEffectOnce } from 'react-use';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Card } from '../CommonComponents/OPL/Card/Card';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { NewsfeedLoading } from './Newsfeed.loading';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { NewsfeedEmpty } from './Newsfeed.empty';
import { useNewsfeed } from './useNewsfeed';
import { LoadingPost } from './components/LoadingPost/LoadingPost';
import { Post } from './components/Post/Post';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { newsfeedQueryKeys } from './api/queryKeys';
import { useClientId } from '../_hooks/useClientId';
import { queryClient } from '../../config/queryClient';

export const Newsfeed = () => {
  useDocumentTitle('Newsfeed');

  return (
    <Box m="auto" width="600px">
      <Text as="h1" fontWeight={500} color="neutral.700" fontSize="h5" mb="8">
        Newsfeed
      </Text>
      <Text fontSize="small" color="neutral.500" mb="32">
        Check out the latest news
      </Text>
      <NewsfeedContent />
    </Box>
  );
};

const NewsfeedContent = () => {
  const clientId = useClientId();

  const {
    data: newsFeedData,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useNewsfeed();

  useEffectOnce(() => {
    queryClient.setQueryData(newsfeedQueryKeys.unreadNewsCount(clientId), 0);
  });

  if (isLoading) {
    return <NewsfeedLoading />;
  }

  if (isError) {
    return (
      <>
        <Text as="h2" variant="smallCaps" mb="16">
          News
        </Text>
        <Card p="32" width="100%">
          <ErrorState onAction={refetch} />
        </Card>
      </>
    );
  }

  if (!newsFeedData) {
    return <NewsfeedEmpty />;
  }

  const { feature, news } = newsFeedData;

  return (
    <>
      <Box display="flex" flexDirection="column">
        {feature.length > 0 && (
          <Text as="h2" variant="smallCaps" mb="16">
            Featured
          </Text>
        )}

        <Box as="ol" p="0" css="list-style-type: none">
          {feature.map((post, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card as="li" p="16" mb="16" key={index}>
              <Post post={post} />
            </Card>
          ))}
        </Box>

        {news.length > 0 && (
          <Text as="h2" variant="smallCaps" mb="16">
            News
          </Text>
        )}

        <Box as="ol" p="0" css="list-style-type: none">
          {news.map((post, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card as="li" p="16" mb="16" key={index}>
              <Post post={post} />
            </Card>
          ))}
        </Box>

        {isFetchingNextPage && <LoadingPost />}
      </Box>

      {hasNextPage && (
        <Box display="flex" flexDirection="column" mt="8">
          <Button
            isLoading={isFetchingNextPage}
            onClick={() => {
              fetchNextPage();
            }}
          >
            Show More
          </Button>
        </Box>
      )}
    </>
  );
};
